import { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import type { useCategoryEditor } from "./use-category-editor";
import { plural } from "../../lib/finance";
import { categoryIcon } from "../../components/CategoryIcon";

// One category tree on the settings screen: displayed as a clear vertical tree with branch connectors.

export default function CategoryGroup({
  editor,
  hasTransactionUse,
}: {
  editor: ReturnType<typeof useCategoryEditor>;
  hasTransactionUse: (name: string, subcategory?: string) => boolean;
}) {
  const [search, setSearch] = useState("");
  const {
    list,
    map,
    newCategory,
    setNewCategory,
    newSubcategory,
    setNewSubcategory,
    addCategory,
    removeCategory,
    renameCategory,
    addSubcategory,
    removeSubcategory,
    renameSubcategory,
  } = editor;

  const filteredList = list.filter((c) =>
    `${c} ${(map[c] || []).join(" ")}`.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <form className="settings-add-row" onSubmit={addCategory}>
        <input
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder="New category"
          aria-label="New category"
        />
        <button type="submit" className="secondary-action">
          <Plus size={16} /> Add
        </button>
      </form>
      <label>
        Find category
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search categories or subcategories"
        />
      </label>
      <div className="category-tree">
        {filteredList.map((category) => {
          const subcategories = map[category] || [];
          const Icon = categoryIcon(category);
          return (
            <div className="category-tree-node" key={category}>
              <div className="category-tree-header">
                <span className="category-tree-icon" aria-hidden="true">
                  <Icon size={16} />
                </span>
                <div className="category-tree-name-wrap">
                  <input
                    defaultValue={category}
                    disabled={category === "Uncategorized"}
                    aria-label={`Category ${category}`}
                    onBlur={(e) => renameCategory(category, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                    }}
                  />
                  <span className="category-tree-count">
                    {plural(subcategories.length, "subcategory", "subcategories")}
                  </span>
                </div>
                <div className="category-tree-actions">
                  <button
                    type="button"
                    className="ghost-icon danger"
                    title={
                      hasTransactionUse(category)
                        ? "Hide from new entries. Existing transactions stay unchanged."
                        : "Remove category"
                    }
                    onClick={() => removeCategory(category)}
                    disabled={category === "Uncategorized"}
                  >
                    <Trash2 size={15} />
                    <span>{hasTransactionUse(category) ? "Hide" : "Remove"}</span>
                  </button>
                </div>
              </div>

              {hasTransactionUse(category) && (
                <p className="settings-note">
                  Used by existing transactions. Removing hides it from new entries only.
                </p>
              )}

              {/* Vertical Tree Branches for Subcategories */}
              <div className="category-tree-branches">
                {subcategories.map((subcategory) => (
                  <div className="category-tree-branch" key={subcategory}>
                    <input
                      defaultValue={subcategory}
                      aria-label={`${category} subcategory ${subcategory}`}
                      onBlur={(e) => renameSubcategory(category, subcategory, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                    />
                    <button
                      type="button"
                      className="ghost-icon"
                      title={
                        hasTransactionUse(category, subcategory)
                          ? "Hide from new entries. Existing transactions stay unchanged."
                          : "Remove subcategory"
                      }
                      onClick={() => removeSubcategory(category, subcategory)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}

                {/* Tree Branch: Add subcategory */}
                <div className="category-tree-branch add">
                  <input
                    value={newSubcategory[category] || ""}
                    disabled={category === "Uncategorized"}
                    placeholder="+ Add subcategory"
                    aria-label={`New subcategory for ${category}`}
                    onChange={(e) =>
                      setNewSubcategory((current) => ({
                        ...current,
                        [category]: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addSubcategory(category);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="secondary-action"
                    onClick={() => addSubcategory(category)}
                    disabled={!newSubcategory[category]}
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
